import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { ITableState } from '@modules/tables/types';

import { PAYMENT_SYSTEMS } from '../constants';

export type IPaymentSystemSettings = typeof PAYMENT_SYSTEMS[number];

export type PaymentSystemName = IPaymentSystemSettings['name'];

const paymentSystemNameSet = new Set<PaymentSystemName>(PAYMENT_SYSTEMS.map(({ name }) => name));

export function isPaymentSystemName(value: any): value is PaymentSystemName {
  return paymentSystemNameSet.has(value);
}

export interface IPaymentSystemField {
  required?: boolean;
}

export interface IPaymentSystemsFields {
  name?: PaymentSystemName;
  country_code?: number;
  account_holders_name?: string;
  branch_code?: string;
  routing_number?: string;
  username?: string;
  poker_nickname?: string;
  email?: string;
  wallet?: string;
  card_number?: string;
  country?: string;
  address?: string;
  city?: string;
  company_name?: string;
  company_country?: string;
  company_city?: string;
  company_address?: string;
  company_postal?: string;
  iban?: string;
  bic?: string;
  bank_name?: string;
  bank_country?: string;
  bank_address?: string;
  bank_city?: string;
  swift?: string;
  phone_number?: string;
}

export type IPaymentSystemFieldNames = keyof IPaymentSystemsFields;

export enum EPaymentSystemStatus {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING = 'pending',
}

export enum EReviewPaymentSystemActionType {
  APPROVE = 'approve',
  REJECT = 'reject',
}

export enum EReviewButtonVariants {
  PRIMARY = 'primary',
  TEXT = 'text',
}

export enum EMassReviewActionType {
  APPROVE = 'mass_approve',
  REJECT = 'mass_reject',
}

export interface IReviewPaymentSystemPayload {
  actionType: EReviewPaymentSystemActionType;
  id: number;
}

export interface IMassReviewPaymentSystemPayload {
  actionType: EMassReviewActionType;
  form_ids?: number[];
  segment_id?: ITableState['filter_set_id'];
  page?: ITableState['page'];
}

export interface ICurrentPaymentInfo extends IPaymentSystemsFields {
  payment_method_short?: string;
}

export interface IContacts {
  telegram?: string;
  whatsapp?: string;
  ms_teams?: string;
  email?: string;
}

export interface IShowCurrentPaymentInfoResponse {
  payment_system?: ICurrentPaymentInfo;
  payment_change_form?: IAffiliateChangePaymentSystemData;
}

export interface IAffiliateChangePaymentSystemData {
  id: number;
  status: EPaymentSystemStatus.PENDING | EPaymentSystemStatus.REJECTED;
  rejection_reason?: string;
  new_payment_method: ICurrentPaymentInfo;
  manager_name?: string;
  manager_contacts?: IContacts;
}

export interface IRelatedPaymentRequestItem {
  id: number;
  created_at: string;
}

export interface IAdminChangePaymentSystemResponse {
  payment_change_form: IAdminChangePaymentSystemData;
  related_requests: IRelatedPaymentRequestItem[];
}

export interface IAdminChangePaymentSystemData {
  id: number;
  affiliate_id: number;
  manager_id: number;
  editor_id: number;
  status: string;
  rejection_reason: string;
  new_payment_method: ICurrentPaymentInfo;
  old_payment_method: ICurrentPaymentInfo;
  new_payment_method_short: string;
  old_payment_method_short: string;
  affiliate_name: string;
  affiliate_email: string;
  affiliate_telegram: string;
  manager_name: string;
  editor_name: string;
  child_id: number | null;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
  affiliate_contacts: IContacts;
}

export interface IAllowedPaymentSystemData {
  required: IPaymentSystemFieldNames[] | Record<number, IPaymentSystemFieldNames[]>;
}

export type IAffiliateAllowedPaymentSystem = Partial<Record<PaymentSystemName, IAllowedPaymentSystemData>>;

export interface IAffiliateAllowedPaymentSystemsResponse {
  payment_systems: IAffiliateAllowedPaymentSystem[];
}

export type IPaymentSystemFormSchema = FormSchema<IPaymentSystemsFields, never, {
  country_code: {
    selectOptionValue: 'country_code';
    selectOptionLabel: 'name';
  };
}>;

export type IPaymentSystemsFormValues = Record<PaymentSystemName, ICurrentPaymentInfo>;

export interface IChangePaymentSystemPayload {
  id?: number;
  payment_system: ICurrentPaymentInfo;
}

export enum EPaymentSystemsTableListType {
  PENDING_LIST = 'pending',
  HISTORY_LIST = 'history',
}

export interface IAdminPaymentSystemsDetailsListRecord {
  id: number;
  affiliate_id: number;
  manager_id: number;
  affiliate_name: string;
  manager_name: string;
  new_payment_method_short: string;
  old_payment_method_short: string;
  new_payment_name?: PaymentSystemName;
  old_payment_name?: PaymentSystemName;
  status?: EPaymentSystemStatus;
  affiliate_email?: string;
  affiliate_telegram?: string;
  editor_id?: number;
  editor_name?: string;
  created_at?: string;
  updated_at?: string;
}
